import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import FeatherIcon from 'components/base/FeatherIcon';
import { useAppSelector, useAppDispatch } from 'redux/store';
import { updateUser, setUserSetting } from 'redux/slices/auth';
import { selectTimeFormat, TimeFormatPreference } from 'redux/selectors/userSettings';

type Option = { value: TimeFormatPreference; label: string; hint: string };

const TIME_FORMAT_OPTIONS: Option[] = [
  {
    value: 'auto',
    label: 'Auto (browser)',
    hint: 'Follow the browser locale default',
  },
  { value: '24h', label: '24-hour', hint: 'e.g. 17:30' },
  { value: '12h', label: '12-hour', hint: 'e.g. 5:30 PM' },
];

const UserSettingsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.auth.userData);
  const user_settings = userData?.user_settings || {};
  const persistedTimeFormat = useAppSelector(selectTimeFormat);

  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState<TimeFormatPreference>(persistedTimeFormat);
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the modal opens, in case the persisted value
  // changed elsewhere while it was closed.
  useEffect(() => {
    if (show) setDraft(persistedTimeFormat);
  }, [show, persistedTimeFormat]);

  const handleOpen = () => setShow(true);
  const handleCancel = () => {
    setDraft(persistedTimeFormat);
    setShow(false);
  };

  const handleSave = async () => {
    if (draft === persistedTimeFormat) {
      setShow(false);
      return;
    }
    setSaving(true);
    // Optimistically reflect the choice so timestamps re-flow immediately,
    // then persist via the server (which returns the merged user record).
    dispatch(setUserSetting({ key: 'timeFormat', value: draft }));
    try {
      await dispatch(
        updateUser({
          user_settings: { ...user_settings, timeFormat: draft },
        })
      );
      setShow(false);
    } catch (err) {
      // updateUser surfaces its own error via the rejected state; keep the
      // modal open so the user can retry or cancel.
      console.error('Failed to save user settings', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip id="user-settings-tooltip" style={{ position: 'fixed' }}>
            User settings
          </Tooltip>
        }
      >
        <div className="nav-icon-circle nav-icon-circle--grey" onClick={handleOpen} role="button" aria-label="Open user settings">
          <FeatherIcon icon="settings" size={16} />
        </div>
      </OverlayTrigger>

      <Modal show={show} onHide={handleCancel} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>User settings</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-bold">Time format</Form.Label>
            <div className="text-muted small mb-2">How dates and times are displayed throughout the app.</div>
            {TIME_FORMAT_OPTIONS.map((opt) => (
              <Form.Check
                key={opt.value}
                type="radio"
                id={`time-format-${opt.value}`}
                name="time-format"
                checked={draft === opt.value}
                onChange={() => setDraft(opt.value)}
                label={
                  <span>
                    <span className="fw-semibold">{opt.label}</span> <span className="text-muted small">— {opt.hint}</span>
                  </span>
                }
                className="mb-1"
              />
            ))}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="phoenix-secondary" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserSettingsModal;
